import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

export interface UserRequest {
  id: number;
  requestType: string;      // PROJET, BIEN, SERVICE, etc.
  operationType: string;    // AJOUT, MODIFICATION, SUPPRESSION
  status: string;           // EN_ATTENTE, APPROUVE, REFUSE
  createdAt: Date;
  updatedAt: Date;
  viewedByAdmin: boolean;
  title: string;
  description: string;
  price: string;
  category: string;
  address: string;
  region: string;
  videoUrl: string;
  imageLinks: string[];
  targetEntityId?: number;
  targetEntityName?: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  userId: number;
  adminComment?: string;
}

export interface UpdatePhotosResponse {
  success: boolean;
  message: string;
  count: number;
}

export interface UpdateVideoResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminRequestService {
  private basePath = 'https://app.titv-store-api.com/';
  private refreshNeeded = new BehaviorSubject<void>(undefined);

  constructor(private http: HttpClient) {}

  get refreshNeeded$() {
    return this.refreshNeeded.asObservable();
  }

  // ---- Token & headers (identical to your BiensImmobs service) ----
  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    if (token) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      });
    }
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Une erreur est survenue';
    if (error.status === 403) {
      errorMessage = `Accès interdit. Token: ${this.getToken() ? 'présent' : 'absent'}`;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    console.error('AdminRequestService Error:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // ---- API Methods ----
  getNotificationsCount(): Observable<{ count: number; message: string }> {
    return this.http.get<{ count: number; message: string }>(
      `${this.basePath}admin/requests/notifications/count`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getNotifications(): Observable<{ count: number; notifications: UserRequest[] }> {
    return this.http.get<{ count: number; notifications: UserRequest[] }>(
      `${this.basePath}admin/requests/notifications`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  markAsRead(requestId: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.basePath}admin/requests/notifications/mark-read/${requestId}`,
      {},
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  markAllAsRead(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.basePath}admin/requests/notifications/mark-all-read`,
      {},
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  getPendingRequests(): Observable<UserRequest[]> {
    return this.http.get<UserRequest[]>(
      `${this.basePath}admin/requests/pending`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getAllRequests(): Observable<UserRequest[]> {
    return this.http.get<UserRequest[]>(
      `${this.basePath}admin/requests/all`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getRequestById(requestId: number): Observable<UserRequest> {
    return this.http.get<UserRequest>(
      `${this.basePath}admin/requests/${requestId}`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  approveRequest(requestId: number, comment?: string): Observable<{ success: boolean; message: string }> {
    let url = `${this.basePath}admin/requests/approve/${requestId}`;
    if (comment) {
      url += `?comment=${encodeURIComponent(comment)}`;
    }
    return this.http.post<{ success: boolean; message: string }>(
      url,
      {},
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  rejectRequest(requestId: number, comment: string): Observable<{ success: boolean; message: string }> {
    const url = `${this.basePath}admin/requests/reject/${requestId}?comment=${encodeURIComponent(comment)}`;
    return this.http.post<{ success: boolean; message: string }>(
      url,
      {},
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  deleteRequest(requestId: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(
      `${this.basePath}admin/requests/delete/${requestId}`,
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  getStats(): Observable<any> {
    return this.http.get<any>(
      `${this.basePath}admin/requests/stats`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /**
   * Update only the photos (imageLinks) of a request.
   * @param requestId ID of the request
   * @param newImageLinks Complete new list of image URLs (empty array to clear all)
   * @returns Observable with success status, message, and count of images
   */
  updatePhotos(requestId: number, newImageLinks: string[]): Observable<UpdatePhotosResponse> {
    return this.http.post<UpdatePhotosResponse>(
      `${this.basePath}admin/requests/update-photos/${requestId}`,
      newImageLinks,
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }

  /**
   * Update the video URL of a request.
   * @param requestId ID of the request
   * @param videoUrl New video URL (pass null or empty string to remove)
   * @returns Observable with success status and message
   */
  updateVideoUrl(requestId: number, videoUrl: string | null): Observable<UpdateVideoResponse> {
    return this.http.post<UpdateVideoResponse>(
      `${this.basePath}admin/requests/update-video/${requestId}`,
      { videoUrl },
      { headers: this.getHeaders() }
    ).pipe(
      tap(() => this.refreshNeeded.next()),
      catchError(this.handleError)
    );
  }
}